import dynamic               from "next/dynamic";
import { useRouter }         from "next/router";
import { useEffect, useRef } from "react";

import { useGetIsBlockedByStreamer } from "_common/hooks/global";
import { useGetDeviceType }          from "_common/hooks/global";
import { getBarHeight }              from "_common/utils";
import Icon                          from "_components/atoms/icon";
import Render                        from "_components/atoms/render";
import ChatBox                       from "_components/features/chat/box";
import { useGetOpenChatManagement }  from "_components/pages/studio/states";
import { useGetUpdateSession }       from "_jotai/liveSession";

const RenderReactions = dynamic ( () => import ( "_components/features/chat/userInteractions/renderChatReactions" ) );
const ChatManagement  = dynamic ( () => import ( "_components/features/chat/management" ) );
const ChatInputField  = dynamic ( () => import ( "_components/features/chat/inputField" ) );
const PinnedComment   = dynamic ( () => import ( "_components/molecules/pinnedComment" ) );
const TopDonationList = dynamic ( () => import ( "_components/molecules/topDonationList" ) );

import useStyles from "./styles";

const component = props => {
	const {
		// broadcasterId
		// changeActiveTab,
		// closeDonationModals,
		// errorModal,
		// handleModalOptions,
		// toggleErrorModal,
		activeTab,
		autoScrollEnabled,
		broadcastId,
		chatPoppedOut,
		comments,
		deleteComment,
		displayModal,
		donationOption,
		emotesList,
		handleCommentOptimistic,
		handleModal,
		isFromLayout,
		isSessionEnded,
		isStreamer,
		leaderboardData,
		myMutedStateData,
		polls,
		pollsCount,
		removeDonor,
		setActiveTab,
		setAutoScroll,
		showPolls,
		theatreModeToggle,
		togglePolls,
		toggleUpdatedComments,
		topDonor,
		userProfile,
		handleDisabledChatInputClick
	} = props;

	const noLeftMargin = isFromLayout || chatPoppedOut;

	const barHeight            = getBarHeight ();
	const deviceType           = useGetDeviceType ();
	const isOpenChatManagement = useGetOpenChatManagement ();
	const isBlockedByStreamer  = useGetIsBlockedByStreamer ();
	const sessionData          = useGetUpdateSession ();
	const sportsFanId          = sessionData?.broadcaster?.sportsFan?.id;
	const streamerName         = sessionData?.broadcaster?.sportsFan?.name;
	const { classes }          = useStyles ( { noLeftMargin, showScrollBtn: !autoScrollEnabled && !isSessionEnded } );
	const router               = useRouter ();
	let commentRef             = useRef ( null );

	const isTablet         = chatPoppedOut ? false : deviceType === "tablet";
	const isMobile         = chatPoppedOut ? false : deviceType === "mobile";
	const isMobileOrTablet = chatPoppedOut ? false : isMobile || isTablet;

	const containerStyle = theatreModeToggle
		? classes.theatreModeOn
		: classes.theatreModeOff;

	const cardWrapperStyle = chatPoppedOut ? `${ containerStyle } ${ classes.theatreViewPopOut }`
		: `${ containerStyle } ${ classes.theatreView }`;

	const isPopOut = router.pathname.includes ( "popOutChat" );

	// const isFanLeaderboardLive = leaderboardData.isLive;

	const onScroll = () => {
		const target = commentRef?.current?.chatArea;

		if ( !target )
			return;

		const height = target.scrollHeight;
		const top    = target.scrollTop;
		const offset = target.offsetHeight;

		let diff = height - top - offset;

		// If the difference is less than 100px, then don't stop the auto scroll
		if ( diff > 150 )
			setAutoScroll ( false );
		else
			setAutoScroll ( true );
	};

	useEffect ( () => {
		if ( !autoScrollEnabled )
			return;

		goToBottom ();
	}, [ comments?.length, autoScrollEnabled ] );

	const goToBottom = () => {
		autoScrollToBottom ( true );
	};

	const autoScrollToBottom = isClicked => {
		const scrollContainer = commentRef?.current?.chatArea;

		if ( !scrollContainer )
			return;

		if ( !autoScrollEnabled && !isClicked )
			return;

		commentRef?.current?.scrollIntoView ();
		// scrollContainer.scrollTo ( { top: scrollContainer.scrollHeight, behavior: "smooth" } );
	};

	return (
		<div className = { cardWrapperStyle }
			style = { {
				height        : isMobile ? `calc((100vh - 56.25vw) - 120px - ${ barHeight }px )` : isTablet ? "50vh" : !isPopOut ? "100%" : "calc(100vh - 55px)",
				width         : isPopOut || isMobileOrTablet ? "100vw" : "25vw",
				display       : "flex",
				flexDirection : "column"
			} }
		>
			<Render condition = { isStreamer && !isSessionEnded }>
				<ChatManagement isOpen = { isOpenChatManagement } />
			</Render>

			<Render condition = { !isStreamer }>
				<RenderReactions />
			</Render>

			<ChatBox
				ref = { commentRef }
				autoScrollEnabled = { autoScrollEnabled }
				autoScrollToBottom = { autoScrollToBottom }
				broadcastId = { broadcastId }
				chatPoppedOut = { chatPoppedOut }
				comments = { comments }
				deleteComment = { deleteComment }
				emotesList = { emotesList }
				handleModal = { handleModal }
				isBlockedByStreamer = { isBlockedByStreamer }
				isLive = { !isSessionEnded }
				isSessionEnded = { isSessionEnded }
				isStreamer = { isStreamer }
				isTabsVisible = { leaderboardData.isLive }
				onScroll = { onScroll }
				setActiveTab = { setActiveTab }
				setAutoScroll = { setAutoScroll }
				sportsFanId = { sportsFanId }
				streamerName = { streamerName }
				togglePolls = { togglePolls }
				toggleUpdatedComments = { toggleUpdatedComments }
			/>

			<Render condition = { !isSessionEnded && broadcastId && !isBlockedByStreamer }>

				<div className = { `bottomPositioned fullWidth ${ classes.chatBottomContainer }` }>

					<div className = { classes.scrollToBottom }>
						<div
							className = { classes.newComment }
							onClick = { goToBottom }
						>
							<div> Scroll to Bottom </div>

							<Icon
								className = { classes.downArrowIcon }
								modeType = "dark"
								name = "caret"
							/>
						</div>
					</div>

					<div style = { { height: "auto" } }>
						<PinnedComment
							broadcastId = { broadcastId }
							emotesList = { emotesList }
							isMobile = { isMobile }
							isStreamer = { isStreamer }
							userProfile = { userProfile }
						/>

						<Render condition = { topDonor?.length }>
							<TopDonationList
								hideDonors = { activeTab !== "CHAT" }
								removeDonor = { removeDonor }
								topDonor = { topDonor }
							/>

						</Render>

						<Render condition = { !isMobile }>

							<ChatInputField
								broadcastId = { parseInt ( broadcastId ) }
								chatPoppedOut = { chatPoppedOut }
								closeEmojiModal = { polls || donationOption || displayModal }
								emotesList = { emotesList }
								handleCommentOptimistic = { handleCommentOptimistic }
								handleDisabledChatInputClick = { handleDisabledChatInputClick }
								handleModal = { handleModal }
								isSessionEnded = { isSessionEnded }
								isStreamer = { isStreamer }
								myMutedStateData = { myMutedStateData }
								pollsCount = { pollsCount }
								showPolls = { showPolls }
								togglePolls = { togglePolls }
							/>
						</Render>

					</div>
				</div>
			</Render>
		</div>
	);
};

export default component;
